/**
 * Supabase Profile Repository
 * Handles user profile management via Supabase
 */

import type {
  IProfileRepository,
  Profile,
  UpdateProfileData,
  OnboardingData,
} from '../interfaces/IProfileRepository'
import { requireSupabaseClient, type ProfileRow } from '@/lib/supabase'

export class SupabaseProfileRepository implements IProfileRepository {
  /**
   * Get user profile by username
   */
  async getProfile(username: string): Promise<Profile | null> {
    const supabase = requireSupabaseClient()

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('username', username)
      .single<ProfileRow>()

    if (error) {
      if (error.code === 'PGRST116') return null // Not found
      throw new Error(`Failed to fetch profile: ${error.message}`)
    }

    return {
      username: data.username,
      displayName: data.display_name || undefined,
      bio: data.bio || undefined,
      phoneNumber: data.phone_number || undefined,
      birthdate: data.birthdate || undefined,
      shippingAddress: data.shipping_address || undefined,
      joinedAt: new Date(data.created_at),
      updatedAt: data.updated_at ? new Date(data.updated_at) : undefined,
      usernameUpdatedAt: data.username_updated_at
        ? new Date(data.username_updated_at)
        : undefined,
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(
    username: string,
    input: UpdateProfileData
  ): Promise<Profile> {
    const supabase = requireSupabaseClient()

    // If username change requested, validate 24-hour cooldown
    if (input.username && input.username !== username) {
      // First, get current profile to check last username change
      const { data: currentProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('username_updated_at')
        .eq('username', username)
        .single<Pick<ProfileRow, 'username_updated_at'>>()

      if (fetchError) {
        throw new Error(`Failed to fetch profile: ${fetchError.message}`)
      }

      // Check 24-hour cooldown
      if (currentProfile?.username_updated_at) {
        const lastChange = new Date(currentProfile.username_updated_at)
        const hoursSinceChange =
          (Date.now() - lastChange.getTime()) / (1000 * 60 * 60)
        if (hoursSinceChange < 24) {
          const hoursRemaining = Math.ceil(24 - hoursSinceChange)
          throw new Error(
            `Username can only be changed once per day. Please wait ${hoursRemaining} more hour${hoursRemaining === 1 ? '' : 's'}.`
          )
        }
      }

      // Check if new username is already taken
      const { data: existingUser } = await supabase
        .from('profiles')
        .select('username')
        .eq('username', input.username)
        .single()

      if (existingUser) {
        throw new Error('This username is already taken.')
      }
    }

    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    }

    // Handle username change
    if (input.username && input.username !== username) {
      updateData.username = input.username
      updateData.username_updated_at = new Date().toISOString()
    }

    if (input.displayName !== undefined)
      updateData.display_name = input.displayName
    if (input.bio !== undefined) updateData.bio = input.bio
    if (input.phoneNumber !== undefined)
      updateData.phone_number = input.phoneNumber
    if (input.birthdate !== undefined) updateData.birthdate = input.birthdate
    if (input.shippingAddress !== undefined)
      updateData.shipping_address = input.shippingAddress

    // Sync phone to Supabase Auth so OTP login works
    if (input.phoneNumber) {
      await supabase.auth.updateUser({ phone: input.phoneNumber })
    }

    const { data, error } = await supabase
      .from('profiles')
      .update(updateData as Record<string, unknown>)
      .eq('username', username)
      .select()
      .single<ProfileRow>()

    if (error) {
      throw new Error(`Failed to update profile: ${error.message}`)
    }

    return {
      username: data.username,
      displayName: data.display_name || undefined,
      bio: data.bio || undefined,
      phoneNumber: data.phone_number || undefined,
      birthdate: data.birthdate || undefined,
      shippingAddress: data.shipping_address || undefined,
      joinedAt: new Date(data.created_at),
      updatedAt: data.updated_at ? new Date(data.updated_at) : undefined,
      usernameUpdatedAt: data.username_updated_at
        ? new Date(data.username_updated_at)
        : undefined,
    }
  }

  /**
   * Check if user has completed onboarding
   * We check if the profile has a display_name set
   */
  async hasCompletedOnboarding(username: string): Promise<boolean> {
    const supabase = requireSupabaseClient()

    const { data, error } = await supabase
      .from('profiles')
      .select('display_name, bio')
      .eq('username', username)
      .single<Pick<ProfileRow, 'display_name' | 'bio'>>()

    if (error) {
      // If no profile exists, onboarding not complete
      if (error.code === 'PGRST116') return false
      throw new Error(`Failed to check onboarding: ${error.message}`)
    }

    // Consider onboarding complete if display_name is set
    return !!data.display_name
  }

  /**
   * Complete onboarding - update profile with initial data
   */
  async completeOnboarding(
    username: string,
    input: OnboardingData
  ): Promise<void> {
    const supabase = requireSupabaseClient()

    const updateData: Record<string, unknown> = {
      display_name: input.displayName || username,
      bio: input.bio || null,
      updated_at: new Date().toISOString(),
    }

    if (input.phoneNumber) {
      updateData.phone_number = input.phoneNumber
      // Sync phone to Supabase Auth so OTP login works
      await supabase.auth.updateUser({ phone: input.phoneNumber })
    }

    const { error } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('username', username)

    if (error) {
      throw new Error(`Failed to complete onboarding: ${error.message}`)
    }
  }
}
